import moment from 'moment';
import { format as formatFn, startOfDay } from 'date-fns';
import { parseFromTimeZone as _parseFromTimeZone } from 'date-fns-timezone';
import { enUS, zhCN, fr, de, it, ja, pt, ru, es } from 'date-fns/locale';

import { logError } from 'shared-services/logger-service';

export const LOCALES: Record<string, Locale> = { en: enUS, zh: zhCN, fr, de, it, ja, pt, ru, es };

const normalizeDay = date => startOfDay(date);

export const getToday = () => startOfDay(new Date());
export const getTodayM = () => moment().startOf('day');

export const getEarliestDate = dates => Math.min(...dates.map(normalizeDay));

export const getLatestDate = dates => Math.max(...dates.map(normalizeDay));

export const normalizeDateForServer = date => {
    if (!date) {
        return undefined;
    }

    try {
        return new Date(date).toISOString();
    } catch (e) {
        return undefined;
    }
}

export const getDatesArray = (start, end) => {
    const days = [];
    let day = start;

    while (day <= end) {
        days.push(day.toDate());
        day = day.clone().add(1, 'd');
    }

    return days;
}

const TIME_FORMATS = {
    '12h': 'h:mma',
    '24h': 'H:mm'
};

const SHORT_TIME_FORMATS = {
    '12h': 'h:mm',
    '24h': 'H:mm'
};

export const getDateFormats = (timeFormat: UserTimeFormat) => {
    const time = TIME_FORMATS[timeFormat];
    const shortTime = SHORT_TIME_FORMATS[timeFormat];

    return {
        'mm/dd/yyyy': {
            shortTime,
            time,
            date: 'MMM d',
            longDate: 'EEEE, MMMM d',
            dateTimeShort: `MMM d, ${time}`,
            dateTime: `MMM d 'at' ${time}`,
            fullDateShort: 'MMM d, yy',
            fullDate: 'MMM d, yyyy',
            machineFriendlyFullDate: 'MM/dd/yyyy',
            fullDateTime: `MMM d, yyyy 'at' ${time}`,
        },

        'dd/mm/yyyy': {
            shortTime,
            time,
            date: 'd MMM',
            longDate: 'EEEE, d MMMM',
            dateTimeShort: `d MMM, ${time}`,
            dateTime: `d MMM 'at' ${time}`,
            fullDateShort: 'd MMM, yy',
            fullDate: 'd MMM, yyyy',
            machineFriendlyFullDate: 'dd/MM/yyyy',
            fullDateTime: `d MMM, yyyy 'at' ${time}`
        },

        'yyyy/mm/dd': {
            shortTime,
            time,
            date: 'MMM d',
            longDate: 'EEEE, MMMM d',
            dateTimeShort: `MMM d, ${time}`,
            dateTime: `MMM d 'at' ${time}`,
            fullDateShort: 'yy, MMM d',
            fullDate: 'yyyy, MMM d',
            machineFriendlyFullDate: 'yyyy/MM/dd',
            fullDateTime: `yyyy, MMM d 'at' ${time}`,
        }
    };
};

export const altDateFormat = (dateFormat: string) => dateFormat.replace(/D/g, 'd').replace(/Y/g, 'y').replace(' \'at\'', '');

export const parseFromTimeZone = (date, timeZone) => {
    let result;
    try {
        result = _parseFromTimeZone(date, { timeZone });;
    } catch (e) {
        result = new Date(date);
    }

    return result;
}

export const formatDate = (date, format) => {
    // TODO: Add localization
    try {
        if (['string', 'number'].includes(typeof date)) {
            date = new Date(date);
        }

        return formatFn(date, format);
    } catch (error) {
        logError('date-time', error, {
            date,
            format
        });
        return '';
    }
}

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export const getFormattedDuration = (input, options) => {
      const ms = input % 1000;
      input = (input - ms) / 1000;
      const secs = input % 60;
      input = (input - secs) / 60;
      const mins = input % 60;
      const hrs = (input - mins) / 60;

      return `${(options?.onlyIfExists && !hrs) ? '' : (hrs.toString().padStart(options?.hoursPadStart || 2, '0') + ':')}${mins.toString().padStart(2, '0')}${options?.seconds === false ? '' : `:${secs.toString().padStart(2, '0')}`}`;
}
